/* global Stimulus */

const pendingSliderUpdates = new Set()
let sliderBatchFrame = null

const scheduleSliderUpdate = (measure) => {
  pendingSliderUpdates.add(measure)
  if (sliderBatchFrame) return
  sliderBatchFrame = requestAnimationFrame(() => {
    sliderBatchFrame = null
    const batch = [...pendingSliderUpdates]
    pendingSliderUpdates.clear()
    batch.map((read) => read()).forEach((write) => write?.())
  })
}

stimulusApp.register(
  "slider",
  class extends Stimulus.Controller {
    static targets = ["track", "item", "dots", "prevArrow", "nextArrow"]

    #measure = null
    #onScroll = null
    #dots = []
    #dotsStrip = null
    #onPopupClosed = null
    #onSliderLeave = null
    #resizeObserver = null
    #isRTL = false
    #index = null

    connect() {
      this.#isRTL = HB.isRTL()
      this.#buildDots()

      this.#measure = () => {
        const trackWidth = this.trackTarget.offsetWidth
        if (trackWidth === 0) return null
        const progress = Math.abs(this.trackTarget.scrollLeft) / trackWidth
        return () => this.#render(progress)
      }

      this.#onScroll = () => scheduleSliderUpdate(this.#measure)

      this.trackTarget.addEventListener("scroll", this.#onScroll)

      this.#resizeObserver = new ResizeObserver(() =>
        scheduleSliderUpdate(this.#measure),
      )
      this.#resizeObserver.observe(this.trackTarget)

      this.#onPopupClosed = () => {
        const track = this.trackTarget
        track.style.scrollSnapType = "none"
        requestAnimationFrame(() => {
          track.style.scrollSnapType = ""
        })
      }
      document.addEventListener("hb:popup-closed", this.#onPopupClosed)

      this.#onSliderLeave = () => {
        this.#hideArrowIfAtBoundary(
          this.hasPrevArrowTarget && this.prevArrowTarget,
        )
        this.#hideArrowIfAtBoundary(
          this.hasNextArrowTarget && this.nextArrowTarget,
        )
      }
      this.element.addEventListener("mouseleave", this.#onSliderLeave)
    }

    disconnect() {
      this.#resizeObserver?.disconnect()
      this.trackTarget.removeEventListener("scroll", this.#onScroll)
      document.removeEventListener("hb:popup-closed", this.#onPopupClosed)
      this.element.removeEventListener("mouseleave", this.#onSliderLeave)
      pendingSliderUpdates.delete(this.#measure)
      this.#clearDots()
    }

    next() {
      const index = this.#activeIndex()
      if (index >= this.itemTargets.length - 1) return
      this.#scrollToIndex(index + 1)
    }

    prev() {
      const index = this.#activeIndex()
      if (index <= 0) return
      this.#scrollToIndex(index - 1)
    }

    goto(event) {
      this.#scrollToIndex(Number(event.params.index))
    }

    #activeIndex() {
      const trackWidth = this.trackTarget.offsetWidth
      if (trackWidth === 0) return 0
      return Math.round(Math.abs(this.trackTarget.scrollLeft) / trackWidth)
    }

    #scrollToIndex(index) {
      const item = this.itemTargets[index]
      if (!item) return
      const left = this.#isRTL
        ? -(index * this.trackTarget.offsetWidth)
        : item.offsetLeft
      this.trackTarget.scrollTo({ left, behavior: "smooth" })
    }

    #buildDots() {
      if (!this.hasDotsTarget) return
      this.#clearDots()
      const strip = document.createElement("div")
      strip.className = "slider--dots-strip"
      this.#dots = this.itemTargets.map(() => {
        const dot = document.createElement("div")
        dot.className = "slider--dot"
        strip.appendChild(dot)
        return dot
      })
      this.#dotsStrip = strip
      this.dotsTarget.appendChild(strip)
    }

    #clearDots() {
      if (!this.hasDotsTarget) return
      this.dotsTarget.innerHTML = ""
      this.#dots = []
      this.#dotsStrip = null
    }

    #render(progress) {
      const index = Math.round(progress)
      const total = this.itemTargets.length
      const maxVisible = 5

      const centerOffset = maxVisible / 2 - 0.5
      const fractionalStart = Math.max(
        0,
        Math.min(progress - centerOffset, total - maxVisible),
      )

      this.#dots.forEach((dot, i) => {
        dot.classList.toggle("slider--dot_active", i === index)

        if (total > maxVisible) {
          const pos = i - fractionalStart
          let scale = 1

          if (pos < 0 || pos > maxVisible - 1) {
            scale = 0.6
          } else if (pos < 1 && fractionalStart > 0) {
            scale = 0.6 + 0.4 * pos
          } else if (
            pos > maxVisible - 2 &&
            fractionalStart + maxVisible < total
          ) {
            scale = 0.6 + 0.4 * (maxVisible - 1 - pos)
          }

          dot.style.transform = scale < 1 ? `scale(${scale})` : ""
        }
      })

      if (this.#dotsStrip && total > maxVisible) {
        const step = 13
        const viewportWidth = maxVisible * 8 + (maxVisible - 1) * 5
        const stripWidth = total * 8 + (total - 1) * 5
        // Use fractionalStart for smooth, continuous offset
        const offset = fractionalStart * step
        const clamped = Math.max(
          0,
          Math.min(offset, stripWidth - viewportWidth),
        )
        this.#dotsStrip.style.transform = `translateX(${
          this.#isRTL ? clamped : -clamped
        }px)`
      }

      this.#updateArrow(
        this.hasPrevArrowTarget && this.prevArrowTarget,
        index === 0,
      )
      this.#updateArrow(
        this.hasNextArrowTarget && this.nextArrowTarget,
        index === total - 1,
      )

      if (index === this.#index) return
      this.#index = index
      this.dispatch("change", { detail: { index } })
    }

    #updateArrow(arrow, atBoundary) {
      if (!arrow) return
      arrow.classList.toggle("slider--arrow_hidden", atBoundary)
      if (!atBoundary) {
        arrow.classList.remove("hidden")
      } else if (!this.element.matches(":hover")) {
        arrow.classList.add("hidden")
      }
    }

    #hideArrowIfAtBoundary(arrow) {
      if (!arrow) return
      arrow.classList.toggle(
        "hidden",
        arrow.classList.contains("slider--arrow_hidden"),
      )
    }
  },
)
